// Notes — three-pane on desktop, drawer-collapsing on smaller
// viewports. Workspaces left, flat note list center, markdown reader
// (with View / Edit / Split modes) right.
//
// Phase 2a: Edit/View toggle, debounced auto-save, save-state pill,
// New / Delete / Rename actions, image paste+drop upload, and body-
// content filtering via notes.search merged with the local title
// filter.
//
// Phase 2b: CodeMirror 6 with markdown grammar, optimistic-lock
// conflict banner (server returns currentSha/currentContent on
// concurrent edits), Split mode that shows the editor and a live
// preview side-by-side, and a templates picker on the New File flow.

import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import "../../styles/notes.css";

import { Icon } from "../../icons";
import { useNotes, type FlatNote, type NotesSaveState } from "../../lib/use-notes";
import { api, type NotesNote, type NotesTemplate } from "../../lib/rpc";
import { render as renderMarkdown } from "../../lib/notes-markdown";
import { NotesEditor } from "./editor";
import { NotesOutline } from "./outline";
import { NotesBacklinks } from "./backlinks";
import { NotesSearchPalette } from "./search-palette";
import { confirm } from "../../components/confirm";

interface NotesViewProps {
  notify: (msg: string) => void;
}

type ReaderMode = "view" | "edit" | "split";

function formatRelative(ms: number): string {
  if (!ms) return "";
  const diff = Math.max(0, Date.now() - ms);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day}d`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.floor(day / 365)}y`;
}

// Pulls "ideas/2026-roadmap.md" → "2026-roadmap" for display in the
// reader header. The trailing-extension regex matches only the last
// segment so multi-dot names (`notes.draft.md`) just drop `.md`.
function fileBase(path: string): string {
  const last = path.split("/").pop() || path;
  return last.replace(/\.[^.]+$/, "");
}

export function NotesView({ notify }: NotesViewProps) {
  const n = useNotes();
  const [activePath, setActivePath] = useState<string | null>(null);
  const [activeNote, setActiveNote] = useState<NotesNote | null>(null);
  const [search, setSearch] = useState("");
  const [bodyHits, setBodyHits] = useState<Set<string>>(new Set());
  // Folder drill-down within the active workspace. Empty string = workspace
  // root. Breadcrumb above the list lets users navigate up; clicking a
  // subfolder row drills in. Search bypasses folder scope (global search
  // across the whole workspace), and clearing search returns to the
  // folder the user was in.
  const [currentFolder, setCurrentFolder] = useState("");
  const [wsOpen, setWsOpen] = useState(false);
  const [readerOpen, setReaderOpen] = useState(false);
  const [mode, setMode] = useState<ReaderMode>("view");
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const [saveState, setSaveState] = useState<NotesSaveState>("idle");
  // Live editor content for the Split preview. Driven by NotesEditor's
  // onChange so the preview tracks each keystroke without a save round-trip.
  const [previewContent, setPreviewContent] = useState<string>("");
  const [conflict, setConflict] = useState<{ currentSha: string; currentContent: string } | null>(null);
  // Bump on "Discard mine" so the editor re-mounts and re-reads the
  // server's content as its initialContent.
  const [editorEpoch, setEditorEpoch] = useState(0);
  // New-note popover state. Open via the "+" in the list header.
  const [newOpen, setNewOpen] = useState(false);
  const [templates, setTemplates] = useState<NotesTemplate[]>([]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const lastSavedRef = useRef<string>("");
  const readerBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => { if (n.error) notify(n.error); }, [n.error, notify]);

  // Global ⌘K / Ctrl+K opens the search palette. We bind to document
  // so the shortcut works from anywhere in the notes view (editor,
  // list, etc.). The palette has its own onKeyDown for Escape so we
  // don't add an Escape handler here.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Click delegation for [[wikilink]] and #tag anchors inside the
  // rendered markdown body. We catch the event at the wrapper rather
  // than attaching per-anchor listeners (the HTML is set via
  // dangerouslySetInnerHTML, so React refs aren't available).
  const onReaderClick = (e: MouseEvent) => {
    const t = e.target as HTMLElement | null;
    if (!t) return;
    const wiki = t.closest<HTMLElement>("a.wikilink");
    if (wiki) {
      e.preventDefault();
      const target = wiki.dataset.wikilink || "";
      const resolved = resolveWikilink(target);
      if (resolved) {
        openNoteByPath(resolved);
      } else {
        notify(`No note found for "${target}".`);
      }
      return;
    }
    const tag = t.closest<HTMLElement>("a.taglink");
    if (tag) {
      e.preventDefault();
      const tagName = tag.dataset.tag || "";
      if (tagName) setSearch(`#${tagName}`);
    }
  };

  // Clear the active note when the workspace changes — its path is
  // root-relative and may not exist in the new root.
  useEffect(() => {
    setActivePath(null);
    setActiveNote(null);
    setReaderOpen(false);
    setMode("view");
    setRenaming(false);
    setConflict(null);
    setNewOpen(false);
    setCurrentFolder("");
  }, [n.activeRoot?.id]);

  // Lazy-read the active note's body when its path changes. We paint
  // from the in-memory cache synchronously so a re-visit feels instant;
  // the network refresh runs in the background and updates `activeNote`
  // only if the SHA actually moved (avoids a re-render flash when the
  // server returns the same bytes we already had).
  useEffect(() => {
    if (!activePath) { setActiveNote(null); setMode("view"); return; }
    const cached = n.peek(activePath);
    if (cached) {
      setActiveNote(cached);
      lastSavedRef.current = cached.content || "";
      setPreviewContent(cached.content || "");
      setSaveState("idle");
      setConflict(null);
    }
    let cancelled = false;
    n.read(activePath).then((note) => {
      if (cancelled || !note) return;
      // Skip the state churn when the refresh returned identical
      // content. Comparing sha256 is cheap and avoids re-rendering
      // marked + the outline for unchanged notes.
      if (cached && cached.sha256 && note.sha256 === cached.sha256) return;
      setActiveNote(note);
      lastSavedRef.current = note.content || "";
      setPreviewContent(note.content || "");
      setSaveState("idle");
      setConflict(null);
    });
    return () => { cancelled = true; };
  }, [activePath, n.activeRoot?.id]);

  // Body search — runs against the server's notes.search index after a
  // short debounce. Empty query clears hits. We union these with the
  // local title filter so the row appears even when its title doesn't
  // match.
  useEffect(() => {
    const q = search.trim();
    if (!q || !n.activeRoot?.id) { setBodyHits(new Set()); return; }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const hits = await api.notesSearch(n.activeRoot!.id, q, 50);
        if (!cancelled) setBodyHits(new Set(hits.map((h) => h.path)));
      } catch {
        if (!cancelled) setBodyHits(new Set());
      }
    }, 250);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [search, n.activeRoot?.id]);

  // Preload templates whenever the root changes — small list, cheap.
  // Used by the New File popover. Read-only roots return [] from
  // server so the picker simply shows "Blank" as the only option.
  useEffect(() => {
    let cancelled = false;
    n.listTemplates().then((t) => { if (!cancelled) setTemplates(t); });
    return () => { cancelled = true; };
  }, [n.activeRoot?.id, n.listTemplates]);

  // Directory-scoped listing for the current folder. Fetched directly
  // via notes.listDir rather than inferred from the flat `n.notes` list
  // — that flat list is built by walkRoot() which caps at MAX_DEPTH and
  // MAX_FILES, AND only records files (not empty directories). Both of
  // those mean folders can be silently invisible if they're deep, late
  // in the walk, or empty. listDir is unfiltered and returns folders
  // even when they have no notes inside.
  interface DirListing { folders: { name: string; path: string }[]; files: FlatNote[] }
  const [dirListing, setDirListing] = useState<DirListing | null>(null);
  const [dirLoading, setDirLoading] = useState(false);

  useEffect(() => {
    if (!n.activeRoot?.id) { setDirListing(null); return; }
    let cancelled = false;
    setDirLoading(true);
    api.notesListDir(n.activeRoot.id, currentFolder)
      .then((entries) => {
        if (cancelled) return;
        const folderPrefix = currentFolder ? currentFolder + "/" : "";
        const folders: { name: string; path: string }[] = [];
        const files: FlatNote[] = [];
        for (const e of entries) {
          // Same hidden-file filter walkRoot uses — `.foo`, `_templates`,
          // `_assets` are reserved for editor plumbing.
          if (e.name.startsWith(".") || e.name === "_templates" || e.name === "_assets") continue;
          if (e.type === "dir") {
            folders.push({ name: e.name, path: folderPrefix + e.name });
          } else {
            const dot = e.name.lastIndexOf(".");
            const ext = dot >= 0 ? e.name.slice(dot + 1).toLowerCase() : "";
            const base = dot >= 0 ? e.name.slice(0, dot) : e.name;
            const path = folderPrefix + e.name;
            files.push({
              path,
              // Title stays plain when scoped to a folder — no need for
              // the " · "-joined ancestor prefix the flat list uses,
              // because the breadcrumb already shows it.
              title: base,
              mtimeMs: typeof e.mtimeMs === "number" ? e.mtimeMs : 0,
              ext,
            });
          }
        }
        folders.sort((a, b) => a.name.localeCompare(b.name));
        files.sort((a, b) => a.title.localeCompare(b.title));
        setDirListing({ folders, files });
      })
      .catch(() => { if (!cancelled) setDirListing({ folders: [], files: [] }); })
      .finally(() => { if (!cancelled) setDirLoading(false); });
    return () => { cancelled = true; };
  }, [n.activeRoot?.id, currentFolder, n.notes]);

  // When the search box has text, ignore folder scope and run a
  // workspace-wide search across the flat list. When empty, show only
  // the items in `dirListing` (the current folder's immediate children).
  const isSearching = search.trim().length > 0;

  const searchedNotes = useMemo(() => {
    if (!isSearching) return [] as FlatNote[];
    const q = search.trim().toLowerCase();
    return n.notes.filter((nt) =>
      nt.title.toLowerCase().includes(q) || bodyHits.has(nt.path),
    );
  }, [isSearching, n.notes, search, bodyHits]);

  const visibleNotes = isSearching
    ? searchedNotes
    : (dirListing?.files ?? []);
  const visibleFolders = isSearching ? [] : (dirListing?.folders ?? []);
  const crumbs = currentFolder ? currentFolder.split("/") : [];

  // Strips the `currentFolder` prefix from a note's display title so
  // rows don't repeat the path the breadcrumb already shows. Search
  // results keep the full path because they may span folders.
  const displayTitle = useCallback((note: FlatNote): string => {
    if (isSearching || !currentFolder) return note.title;
    const prefix = currentFolder.replace(/\//g, " · ") + " · ";
    return note.title.startsWith(prefix) ? note.title.slice(prefix.length) : note.title;
  }, [isSearching, currentFolder]);

  const onPickNote = (note: FlatNote) => {
    setActivePath(note.path);
    setReaderOpen(true);
    setMode("view");
    setRenaming(false);
  };

  // Used by wikilink and search-palette jumps. Updates `currentFolder`
  // to the target note's parent so closing the reader returns the user
  // to that note's folder context, not the folder they came from.
  const openNoteByPath = useCallback((path: string) => {
    const slash = path.lastIndexOf("/");
    setCurrentFolder(slash >= 0 ? path.slice(0, slash) : "");
    setActivePath(path);
    setReaderOpen(true);
    setMode("view");
    setRenaming(false);
  }, []);

  // Lowercase filename-stem → relPath map so wikilink resolution
  // matches the server's title-resolution exactly. Built off the flat
  // note list; rebuilds whenever the list changes (new note, rename,
  // delete).
  const titleIndex = useMemo(() => {
    const map = new Map<string, string>();
    for (const note of n.notes) {
      const base = note.path.split("/").pop() || note.path;
      const stem = base.replace(/\.[^.]+$/, "").toLowerCase();
      map.set(stem, note.path);
      // Also accept the full lowercased relpath so `[[folder/Foo]]`
      // resolves directly without going through the title map.
      map.set(note.path.toLowerCase(), note.path);
      map.set(note.path.toLowerCase().replace(/\.[^.]+$/, ""), note.path);
    }
    return map;
  }, [n.notes]);

  const resolveWikilink = useCallback((target: string): string | null => {
    const t = target.trim().toLowerCase();
    if (!t) return null;
    return titleIndex.get(t)
        || titleIndex.get(t.replace(/\.md$/i, ""))
        || titleIndex.get(`${t}.md`)
        || null;
  }, [titleIndex]);

  const renderedBody = useMemo(() => {
    if (!activeNote || !activeNote.content) return "";
    // Code / unknown kinds get rendered as a single fenced block; the
    // markdown helper handles markdown directly. notesReadNote returns
    // `kind` in {"markdown","preformatted","text","binary"}.
    if (activeNote.kind === "preformatted" || activeNote.kind === "text") {
      const fence = "```\n" + activeNote.content + "\n```";
      return renderMarkdown(fence, { resolveWikilink });
    }
    return renderMarkdown(activeNote.content || "", { resolveWikilink });
  }, [activeNote, resolveWikilink]);

  const previewHtml = useMemo(() => {
    // Split-view preview tracks the editor live; outside split it just
    // mirrors the active note so toggling back to View doesn't flash.
    if (!previewContent) return "";
    if (activeNote?.kind === "preformatted" || activeNote?.kind === "text") {
      const fence = "```\n" + previewContent + "\n```";
      return renderMarkdown(fence, { resolveWikilink });
    }
    return renderMarkdown(previewContent, { resolveWikilink });
  }, [previewContent, activeNote?.kind, resolveWikilink]);

  const canEdit = !!n.activeRoot?.writable && !!activeNote;
  const isEditing = mode !== "view";

  // Ref so the debounced editor callback can early-return when a
  // conflict is already pending — keystrokes still fire onSave (the
  // CM6 listener doesn't know about parent state), so without this
  // guard we'd hammer the server with conflicting saves on every key.
  const conflictRef = useRef<{ currentSha: string; currentContent: string } | null>(null);
  conflictRef.current = conflict;

  const onSave = async (content: string) => {
    if (!activePath) return;
    if (content === lastSavedRef.current) { setSaveState("saved"); return; }
    // Block further saves until the user resolves the existing
    // conflict. The pill stays on "Conflict" so they know we're
    // waiting on them, not on the network.
    if (conflictRef.current) { setSaveState("conflict"); return; }
    setSaveState("saving");
    const r = await n.save(activePath, content, activeNote?.sha256);
    if (r.ok) {
      lastSavedRef.current = content;
      // Detect the offline-queued path: the wrapper synthesizes a
      // response with sha256: "_pending" and _queued: true so we can
      // distinguish "server confirmed" from "queued for later sync."
      // Either way the local content is canonical until the drainer
      // replays.
      const queued = r.sha256 === "_pending" || (r as any)._queued === true;
      setSaveState(queued ? "queued" : "saved");
      setConflict(null);
      setActiveNote((prev) => prev ? { ...prev, content, sha256: r.sha256, mtimeMs: Date.now() } : prev);
      n.refreshNotes();
      return;
    }
    if (r.error === "conflict" && r.currentSha && typeof r.currentContent === "string") {
      // Optimistic-lock failure: another tab/device saved between our
      // read and our save. Show the banner and pause auto-save until
      // the user picks a side.
      setSaveState("conflict");
      setConflict({ currentSha: r.currentSha, currentContent: r.currentContent });
      return;
    }
    setSaveState("error");
    notify(r.error || "Save failed");
  };

  const onDiscardLocal = () => {
    if (!conflict || !activePath) return;
    // Reset to the server's version. Updating the activeNote (sha256 +
    // content) and bumping editorEpoch re-keys NotesEditor so its
    // initialContent re-reads from the new activeNote.
    setActiveNote((prev) => prev ? { ...prev, content: conflict.currentContent, sha256: conflict.currentSha } : prev);
    lastSavedRef.current = conflict.currentContent;
    setPreviewContent(conflict.currentContent);
    setConflict(null);
    setSaveState("idle");
    setEditorEpoch((v) => v + 1);
  };

  const onOverwriteRemote = async () => {
    if (!activePath || !conflict) return;
    // Force-save: skip expectedSha so the server stops checking. The
    // server still rewrites cleanly; whatever was on disk is replaced.
    setSaveState("saving");
    const r = await n.save(activePath, previewContent);
    if (r.ok) {
      lastSavedRef.current = previewContent;
      setSaveState("saved");
      setConflict(null);
      setActiveNote((prev) => prev ? { ...prev, content: previewContent, sha256: r.sha256, mtimeMs: Date.now() } : prev);
      n.refreshNotes();
    } else {
      setSaveState("error");
      notify(r.error || "Save failed");
    }
  };

  const onNewClick = async () => {
    if (!n.activeRoot?.writable) { notify("This workspace is read-only."); return; }
    // Refresh templates each open in case the user added one via the
    // file system between visits. Cheap (a stat + readdir).
    n.listTemplates().then(setTemplates);
    setNewOpen(true);
  };

  const onCreateNote = async (rawName: string, tplPath: string | null) => {
    const name = rawName.trim().replace(/[\\:*?"<>|]/g, "");
    if (!name) return;
    const fileName = /\.[^./]+$/.test(name) ? name : `${name}.md`;
    // If the user is browsing a subfolder, drop the new note there
    // rather than at the workspace root. The user can still override
    // by typing a path with slashes in the name field.
    const path = !fileName.includes("/") && currentFolder
      ? `${currentFolder}/${fileName}`
      : fileName;
    let content = "";
    if (tplPath) {
      const tpl = await n.read(tplPath);
      content = tpl?.content || "";
    }
    const r = await n.create(path, content);
    if (!r.ok) { notify(r.error || "Could not create note."); return; }
    setNewOpen(false);
    setActivePath(path);
    setReaderOpen(true);
    setMode("edit");
  };

  // Shared delete-by-path so the list-row trash icon and the
  // reader-toolbar trash button both go through one confirm + RPC
  // path. Only the active-note case needs to clear the reader state;
  // deleting a non-active note just refreshes the list.
  const deleteNoteByPath = async (path: string) => {
    if (!n.activeRoot?.writable) {
      notify("This workspace is read-only.");
      return;
    }
    const ok = await confirm({
      title: `Delete "${fileBase(path)}"?`,
      message: "The note and its history go away. This can't be undone.",
      confirmLabel: "Delete note",
      danger: true,
    });
    if (!ok) return;
    const r = await n.remove(path);
    if (!r.ok) { notify(r.error || "Delete failed"); return; }
    if (activePath === path) {
      setActivePath(null);
      setActiveNote(null);
      setReaderOpen(false);
      setMode("view");
    }
  };

  const onDelete = async () => {
    if (!activePath || !n.activeRoot?.writable) return;
    await deleteNoteByPath(activePath);
  };

  const startRename = () => {
    if (!activePath) return;
    setRenameDraft(fileBase(activePath));
    setRenaming(true);
  };

  const commitRename = async () => {
    if (!activePath) { setRenaming(false); return; }
    const next = renameDraft.trim().replace(/[\\:*?"<>|]/g, "");
    const oldBase = fileBase(activePath);
    if (!next || next === oldBase) { setRenaming(false); return; }
    const dir = activePath.includes("/") ? activePath.slice(0, activePath.lastIndexOf("/") + 1) : "";
    const ext = activePath.match(/\.[^./]+$/)?.[0] || ".md";
    const newPath = /\.[^./]+$/.test(next) ? `${dir}${next}` : `${dir}${next}${ext}`;
    if (newPath === activePath) { setRenaming(false); return; }
    const r = await n.rename(activePath, newPath);
    if (!r.ok) { notify(r.error || "Rename failed"); setRenaming(false); return; }
    setActivePath(r.newPath || newPath);
    setRenaming(false);
  };

  return (
    <div className="notes-shell" data-ws-open={wsOpen} data-reader-open={readerOpen} data-mode={mode}>
      {wsOpen && <div className="notes-ws-backdrop" onClick={() => setWsOpen(false)} />}

      <aside className="notes-workspaces">
        <div className="notes-ws-head">
          <Icon name="folder" size={14} />
          <span className="notes-ws-head-title">Workspaces</span>
        </div>
        <div className="notes-ws-list">
          {n.loadingRoots
            ? <p style={{ color: "var(--text-dim)", fontSize: 12, padding: 8 }}>loading…</p>
            : n.roots.length === 0
              ? <p style={{ color: "var(--text-dim)", fontSize: 12, padding: 8, fontStyle: "italic" }}>No workspaces yet.</p>
              : n.roots.map((r) => (
                  <button
                    key={r.id}
                    className="notes-ws-row"
                    data-active={n.activeRoot?.id === r.id}
                    onClick={() => { n.setActiveRoot(r.id); setWsOpen(false); }}
                    type="button"
                  >
                    <span
                      className="notes-ws-dot"
                      style={r.color ? { background: r.color, boxShadow: `0 0 8px ${r.color}` } : undefined}
                    />
                    <span className="notes-ws-row-name">{r.label}</span>
                    {!r.writable && <span className="notes-ws-row-ro">read</span>}
                  </button>
                ))
          }
        </div>
      </aside>

      <section className="notes-list">
        <div className="notes-list-head">
          <div className="notes-list-head-row">
            <button
              className="notes-list-trigger"
              onClick={() => setWsOpen(true)}
              type="button"
            >
              <span
                className="notes-ws-dot"
                style={n.activeRoot?.color ? { background: n.activeRoot.color, boxShadow: `0 0 8px ${n.activeRoot.color}` } : undefined}
              />
              <span className="notes-list-title">
                {n.activeRoot?.label || "Notes"}
              </span>
              <span className="notes-list-trigger-chev" aria-hidden>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </span>
            </button>
            {n.activeRoot?.writable && (
              <button
                className="notes-list-new"
                onClick={onNewClick}
                title="New note"
                type="button"
              >
                <Icon name="plus" size={14} />
              </button>
            )}
          </div>
          <div className="notes-list-search">
            <Icon name="search" size={13} />
            <input
              placeholder="Filter notes…"
              value={search}
              onInput={(e) => setSearch((e.currentTarget as HTMLInputElement).value)}
            />
          </div>
          {!isSearching && crumbs.length > 0 && (
            <nav className="notes-crumbs" aria-label="Folder breadcrumb">
              <button
                className="notes-crumb"
                onClick={() => setCurrentFolder("")}
                type="button"
                title="Back to workspace root"
              >
                {n.activeRoot?.label || "Notes"}
              </button>
              {crumbs.map((seg, i) => {
                const target = crumbs.slice(0, i + 1).join("/");
                const isLast = i === crumbs.length - 1;
                return (
                  <span key={target} className="notes-crumb-pair">
                    <span className="notes-crumb-sep" aria-hidden>/</span>
                    {isLast ? (
                      <span className="notes-crumb notes-crumb-current">{seg}</span>
                    ) : (
                      <button
                        className="notes-crumb"
                        onClick={() => setCurrentFolder(target)}
                        type="button"
                      >
                        {seg}
                      </button>
                    )}
                  </span>
                );
              })}
            </nav>
          )}
          {newOpen && (
            <NewNotePopover
              templates={templates}
              onCancel={() => setNewOpen(false)}
              onCreate={onCreateNote}
            />
          )}
        </div>

        <div className="notes-list-scroll">
          {(n.loadingNotes || (dirLoading && !dirListing))
            ? <p style={{ color: "var(--text-dim)", fontSize: 12, padding: 8 }}>loading…</p>
            : visibleNotes.length === 0 && visibleFolders.length === 0
              ? (
                <div className="notes-list-empty">
                  <Icon name="doc" size={32} />
                  <div className="notes-list-empty-line">
                    {n.notes.length === 0
                      ? "No notes yet."
                      : isSearching
                        ? "No matches."
                        : "This folder is empty."}
                  </div>
                </div>
              )
              : (
                <>
                  {visibleFolders.map((f) => (
                    <button
                      key={"folder:" + f.path}
                      className="notes-list-row notes-list-folder"
                      onClick={() => setCurrentFolder(f.path)}
                      type="button"
                    >
                      <div className="notes-list-row-title">
                        <Icon name="folder" size={13} />
                        <span>{f.name}</span>
                      </div>
                      <div className="notes-list-row-meta">folder</div>
                    </button>
                  ))}
                  {visibleNotes.map((note) => (
                    <div
                      key={note.path}
                      className="notes-list-row"
                      data-active={activePath === note.path}
                      onClick={() => onPickNote(note)}
                    >
                      <div className="notes-list-row-body">
                        <div className="notes-list-row-title">{displayTitle(note)}</div>
                        <div className="notes-list-row-meta">
                          {note.ext ? `.${note.ext}` : "file"} · {formatRelative(note.mtimeMs)}
                        </div>
                      </div>
                      {/* Per-row delete — hover-revealed on desktop,
                        * always-visible on mobile (parallels the chat
                        * sidebar pattern). Stop propagation so the row's
                        * onClick doesn't fire and open the note while
                        * the user is trying to delete it. Hidden on
                        * read-only workspaces (the underlying
                        * deleteNoteByPath also no-ops there). */}
                      {n.activeRoot?.writable && (
                        <button
                          className="notes-list-row-delete icon-btn"
                          title="Delete note"
                          onClick={(e) => { e.stopPropagation(); deleteNoteByPath(note.path); }}
                          type="button"
                        >
                          <Icon name="trash" size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </>
              )
          }
        </div>
      </section>

      <section className="notes-reader">
        {!activePath ? (
          <div className="notes-reader-empty">
            <div>
              <Icon name="doc" size={48} />
              <p className="h-italic">Pick a note to read it here.</p>
            </div>
          </div>
        ) : (
          <>
            <header className="notes-reader-head">
              <button
                className="icon-btn notes-reader-back"
                onClick={() => setReaderOpen(false)}
                title="Back"
                type="button"
              >
                <Icon name="arrow-left" size={14} />
              </button>
              {renaming ? (
                <input
                  className="notes-reader-rename"
                  autoFocus
                  value={renameDraft}
                  onInput={(e) => setRenameDraft((e.currentTarget as HTMLInputElement).value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                    if (e.key === "Escape") { e.preventDefault(); setRenaming(false); }
                  }}
                />
              ) : (
                <button
                  className="notes-reader-title notes-reader-title-btn"
                  onClick={canEdit ? startRename : undefined}
                  title={canEdit ? "Rename" : undefined}
                  type="button"
                  disabled={!canEdit}
                >
                  {fileBase(activePath)}
                </button>
              )}
              {isEditing && <SaveStatePill state={saveState} />}
              {activeNote && !isEditing && (
                <span className="notes-reader-meta">
                  {activeNote.kind || "markdown"}
                  {activeNote.mtimeMs ? ` · ${formatRelative(activeNote.mtimeMs)}` : ""}
                </span>
              )}
              <div className="notes-reader-actions">
                <button
                  className="icon-btn"
                  onClick={() => setPaletteOpen(true)}
                  title="Search notes (⌘K)"
                  type="button"
                >
                  <Icon name="search" size={14} />
                </button>
                {canEdit && (
                  <>
                    <ModeToggle mode={mode} setMode={setMode} />
                    <button
                      className="icon-btn"
                      onClick={onDelete}
                      title="Delete note"
                      type="button"
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </>
                )}
              </div>
            </header>

            {conflict && isEditing && (
              <ConflictBanner
                onDiscardLocal={onDiscardLocal}
                onOverwriteRemote={onOverwriteRemote}
                onDismiss={() => setConflict(null)}
              />
            )}

            <div className="notes-reader-body" data-mode={mode} ref={readerBodyRef} onClick={onReaderClick}>
              {isEditing && activeNote ? (
                <>
                  <NotesEditor
                    key={`${activePath}:${editorEpoch}`}
                    initialContent={activeNote.content || ""}
                    onSave={onSave}
                    onDirty={() => setSaveState("dirty")}
                    onChange={setPreviewContent}
                    uploadAsset={n.uploadAsset}
                    saveState={saveState}
                    notify={notify}
                  />
                  {mode === "split" && (
                    <div className="notes-reader-body-inner notes-split-preview"
                         dangerouslySetInnerHTML={{ __html: previewHtml }} />
                  )}
                </>
              ) : (
                <>
                  <div className="notes-reader-body-content">
                    <div
                      className="notes-reader-body-inner"
                      dangerouslySetInnerHTML={{ __html: renderedBody }}
                    />
                    <NotesBacklinks
                      activePath={activePath}
                      epoch={activeNote?.sha256 || activeNote?.mtimeMs || 0}
                      fetcher={n.backlinks}
                      onPick={openNoteByPath}
                    />
                  </div>
                  <NotesOutline content={activeNote?.content || ""} scrollContainer={readerBodyRef.current} />
                </>
              )}
            </div>
          </>
        )}
      </section>

      <NotesSearchPalette
        open={paletteOpen}
        rootId={n.activeRoot?.id || null}
        notes={n.notes}
        onClose={() => setPaletteOpen(false)}
        onPick={openNoteByPath}
      />
    </div>
  );
}

function SaveStatePill({ state }: { state: NotesSaveState }) {
  const label = state === "saving"   ? "Saving…"
    : state === "saved"    ? "Saved"
    : state === "queued"   ? "Queued · offline"
    : state === "dirty"    ? "Unsaved"
    : state === "conflict" ? "Conflict"
    : state === "error"    ? "Save error"
    : "";
  if (!label) return null;
  return (
    <span className="notes-save-pill" data-state={state}>
      {label}
    </span>
  );
}

interface ModeToggleProps {
  mode: ReaderMode;
  setMode: (m: ReaderMode) => void;
}
function ModeToggle({ mode, setMode }: ModeToggleProps) {
  return (
    <div className="notes-mode-toggle" role="tablist">
      <button
        type="button"
        data-active={mode === "view"}
        onClick={() => setMode("view")}
        title="Read mode"
      >View</button>
      <button
        type="button"
        data-active={mode === "edit"}
        onClick={() => setMode("edit")}
        title="Edit only"
      >Edit</button>
      <button
        type="button"
        data-active={mode === "split"}
        onClick={() => setMode("split")}
        title="Edit and preview side-by-side"
      >Split</button>
    </div>
  );
}

interface ConflictBannerProps {
  onDiscardLocal: () => void;
  onOverwriteRemote: () => void;
  onDismiss: () => void;
}
function ConflictBanner({ onDiscardLocal, onOverwriteRemote, onDismiss }: ConflictBannerProps) {
  return (
    <div className="notes-conflict-banner" role="alert">
      <Icon name="warn" size={14} />
      <div className="notes-conflict-text">
        <strong>This note was changed somewhere else.</strong>{" "}
        Your edits aren't saved yet — pick a side.
      </div>
      <button className="notes-conflict-btn" type="button" onClick={onDiscardLocal}>
        Use server version
      </button>
      <button className="notes-conflict-btn notes-conflict-btn-danger" type="button" onClick={onOverwriteRemote}>
        Overwrite anyway
      </button>
      <button className="notes-conflict-dismiss" type="button" onClick={onDismiss} title="Hide">
        <Icon name="x" size={12} />
      </button>
    </div>
  );
}

interface NewNotePopoverProps {
  templates: NotesTemplate[];
  onCancel: () => void;
  onCreate: (name: string, templatePath: string | null) => void;
}
function NewNotePopover({ templates, onCancel, onCreate }: NewNotePopoverProps) {
  const [name, setName] = useState("");
  const [tpl, setTpl] = useState<string>("");
  return (
    <div className="notes-new-popover" role="dialog" aria-label="New note">
      <input
        className="notes-new-name"
        placeholder="Note name (extension optional)"
        autoFocus
        value={name}
        onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); onCreate(name, tpl || null); }
          if (e.key === "Escape") { e.preventDefault(); onCancel(); }
        }}
      />
      {templates.length > 0 && (
        <label className="notes-new-tpl">
          <span>Template</span>
          <select
            value={tpl}
            onChange={(e) => setTpl((e.currentTarget as HTMLSelectElement).value)}
          >
            <option value="">Blank</option>
            {templates.map((t) => (
              <option key={t.path} value={t.path}>{t.name.replace(/\.md$/i, "")}</option>
            ))}
          </select>
        </label>
      )}
      <div className="notes-new-actions">
        <button className="btn-ghost" type="button" onClick={onCancel}>Cancel</button>
        <button
          className="btn-primary"
          type="button"
          onClick={() => onCreate(name, tpl || null)}
          disabled={!name.trim()}
        >Create</button>
      </div>
    </div>
  );
}
